import {
  Component,
  signal,
  ChangeDetectionStrategy,
  computed,
  inject,
  DestroyRef,
  effect,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { SortEvent, ConfirmationService } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { MultiSelectModule } from 'primeng/multiselect';
import { ChipModule } from 'primeng/chip';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService } from 'primeng/dynamicdialog';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormControl,
  Validators,
} from '@angular/forms';
import { Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  switchMap,
} from 'rxjs/operators';
import { UserService } from './users.service';
import { RolesService } from '../roles/roles.service';
import { SkillsService } from '@tourfold/features/settings/skills';
import { TagsService } from '@tourfold/features/settings/tags';
import {
  User,
  UserList,
  UserStatusEnum,
  GetUsersStatusEnum,
  PatchUser,
  CreateUser,
  Role,
  Skill,
  UpdateUserSkills,
  UpdateUserTags,
  UserSkill,
  UserTag,
  TagResponse,
} from '@tourfold/libraries/tf-api-client-public-v2';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { nonEmptyOrUndefined, ServerFieldErrors } from '@tourfold/libraries/shared-util';
import {
  SettingsListHeader,
  SettingsLoadingPlaceholder,
  SimpleChipComponent,
  TfDataTableComponent,
  TfColumnDefDirective,
  ColumnDef,
  TfPageEvent,
  ActionClickEvent,
  MenuItemDef,
} from '@tourfold/libraries/shared-ui';
import { ResourceAccessService } from '@tourfold/shared-kernels/resource-access';
import { provideUsersAiPageContext } from './users-list.ai-page-context';
import {
  UserFiltersDialogComponent,
  type UserFiltersDialogData,
  type UserFiltersDialogResult,
} from './user-filters-dialog/user-filters-dialog.component';
import { UserFormComponent } from './user-form/user-form.component';
import {
  resolveSettingsLoadErrorDetails,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsLoadErrorDetails,
} from '@tourfold/features/settings-ui';

type FilterChipKind = 'status' | 'role' | 'skill' | 'tag';
interface FilterChip {
  kind: FilterChipKind;
  value: string;
  label: string;
}

interface UserRowVm {
  displayName: string;
  roleDisplay: string;
  visibleSkills: UserSkill[];
  hiddenSkillsCount: number;
  hiddenSkillsLabel: string;
  hasSkills: boolean;
  visibleTags: UserTag[];
  hiddenTagsCount: number;
  hiddenTagsLabel: string;
  hasTags: boolean;
}

type RowMenuActionId =
  | 'edit'
  | 'transferOwnership'
  | 'resendSetPasswordEmail'
  | 'activate'
  | 'deactivate'
  | 'unblock'
  | 'delete';

type UserRow = User & { vm: UserRowVm };

type EditUserSaveResult =
  | { user: User; skillsUpdateFailed: false; tagsUpdateFailed: false }
  | { user: User; skillsUpdateFailed: true; tagsUpdateFailed: false; error: unknown }
  | { user: User; skillsUpdateFailed: false; tagsUpdateFailed: true; error: unknown }
  | { user: User; skillsUpdateFailed: true; tagsUpdateFailed: true; error: unknown };

@Component({
  selector: 'tf-settings-users-list',
  imports: [
    InputTextModule,
    ButtonModule,
    TooltipModule,
    MultiSelectModule,
    ChipModule,
    DialogModule,
    SelectModule,
    ConfirmDialogModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    SettingsListHeader,
    SettingsLoadingPlaceholder,
    SimpleChipComponent,
    TfDataTableComponent,
    TfColumnDefDirective,
    UserFormComponent,
    SettingsInlineMessage,
    SettingsLoadError,
  ],
  templateUrl: './users-list.html',
  styleUrl: './users-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService, DialogService],
})
export class Users {
  private readonly userService = inject(UserService);
  private readonly rolesService = inject(RolesService);
  private readonly skillsService = inject(SkillsService);
  private readonly tagsService = inject(TagsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly resourceAccessService = inject(ResourceAccessService);

  readonly canCreateUser = computed(() => this.resourceAccessService.canPerform('users', 'create'));
  readonly canUpdateUser = computed(() => this.resourceAccessService.canPerform('users', 'update'));
  readonly canDeleteUser = computed(() => this.resourceAccessService.canPerform('users', 'delete'));
  readonly canReadSkills = computed(() => this.resourceAccessService.canPerform('skills', 'read'));
  readonly canAssignSkillsOnCreate = computed(() => this.canCreateUser() && this.canReadSkills());
  readonly canAssignSkillsOnEdit = computed(() => this.canUpdateUser() && this.canReadSkills());
  readonly canTransferOwnership = computed(() =>
    this.resourceAccessService.canPerform('ownershipTransfer', 'create')
  );
  private readonly searchSubject = new Subject<string>();
  private readonly translateService = inject(TranslateService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly dialogService = inject(DialogService);
  private readonly fb = inject(FormBuilder);
  readonly users = signal<User[]>([]);
  readonly isLoading = signal(false);
  // Drives the spinner on the create/save buttons while a create or edit request is in flight,
  // kept separate from isLoading so it doesn't trigger the table's loading overlay.
  readonly isSaving = signal(false);
  readonly hasLoadedOnce = signal(false);
  readonly initialLoadFailed = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly isEditDialogVisible = signal<boolean>(false);
  readonly isCreateDialogVisible = signal<boolean>(false);
  readonly searchQuery = signal<string>('');
  readonly totalRecords = signal(0);
  readonly originalUser = signal<User | null>(null); // Store original user for lockVersion
  readonly originalUserRoles = signal<string[]>([]); // Store original role identifiers from backend
  readonly originalUserSkillIds = signal<string[]>([]);
  readonly originalUserTagIds = signal<string[]>([]);
  // Available tag options for the dialog multi-select. Mirrors the role/skill option shape so the
  // pickers in the create/edit dialog all use the same PrimeNG p-multiSelect pattern.
  readonly availableTagOptions = signal<{ label: string; value: string; color: string }[]>([]);
  private tagsLoaded = false;
  private tagsLoadInProgress = false;
  readonly sortField = signal<string | null>(null);
  readonly sortOrder = signal<number>(0); // 1 for asc, -1 for desc, 0 for no sort
  readonly currentUserId = signal<string | null>(null); // Store current logged-in user ID
  readonly first = signal<number>(0); // Track current page offset for pagination
  readonly pageSize = signal<number>(50); // Track selected rows-per-page for pagination
  private skillsLoaded = false;
  private skillsLoadInProgress = false;

  // Reactive forms
  readonly editUserForm = this.fb.group({
    firstname: new FormControl<string>(''),
    lastname: new FormControl<string>(''),
    // Email cannot be changed from the user edit dialog; keep it disabled so the
    // value is still submitted via getRawValue() but the input is not editable.
    email: new FormControl<string>({ value: '', disabled: true }, [Validators.required, Validators.email]),
    roles: new FormControl<string[]>([]), // Remove Validators.required, we handle it in isEditFormValid
    skills: new FormControl<string[]>([]),
    tags: new FormControl<string[]>([]),
  });

  readonly createUserForm = this.fb.group({
    firstname: new FormControl<string>('', [Validators.required]),
    lastname: new FormControl<string>('', [Validators.required]),
    email: new FormControl<string>('', [Validators.required, Validators.email]),
    roles: new FormControl<string[]>([], [Validators.required]),
    skills: new FormControl<string[]>([]),
    tags: new FormControl<string[]>([]),
  });

  // Server-side validation errors (populated from RFC 9457 ProblemDetail responses).
  // The wire field `roleIds` is mapped onto the form control `roles`.
  readonly createServerErrors = new ServerFieldErrors(this.createUserForm, {
    pointerToField: {
      roleIds: 'roles',
      skillIds: 'skills',
      skill_ids: 'skills',
      tagIds: 'tags',
      tag_ids: 'tags',
    },
  });
  readonly editServerErrors = new ServerFieldErrors(this.editUserForm, {
    pointerToField: {
      roleIds: 'roles',
      skillIds: 'skills',
      skill_ids: 'skills',
      tagIds: 'tags',
      tag_ids: 'tags',
    },
  });

  // Filter state
  readonly filterState = signal<UserFiltersDialogResult>({
    statuses: [],
    roleIds: [],
    skillIds: [],
    skillLabels: {},
    tagIds: [],
    tagLabels: {},
    tagColors: {},
  });

  readonly activeFilterCount = computed(
    () =>
      this.filterState().statuses.length +
      this.filterState().roleIds.length +
      this.filterState().skillIds.length +
      this.filterState().tagIds.length
  );

  readonly activeFilterChips = computed<FilterChip[]>(() => {
    const t = (key: string): string => this.translateService.instant(key);
    const { statuses, roleIds, skillIds, skillLabels, tagIds, tagLabels } = this.filterState();
    return [
      ...statuses.map<FilterChip>((s) => ({
        kind: 'status',
        value: s,
        label: `${t('SETTINGS_ACCESS.USERS.STATUS')}: ${this.getStatusDisplay(s)}`,
      })),
      ...roleIds.map<FilterChip>((id) => ({
        kind: 'role',
        value: id,
        label: `${t('SETTINGS_ACCESS.USERS.ROLE')}: ${this.getRoleLabel(id)}`,
      })),
      ...skillIds.map<FilterChip>((id) => ({
        kind: 'skill',
        value: id,
        label: `${t('SETTINGS_ACCESS.USERS.SKILL')}: ${skillLabels[id] ?? id}`,
      })),
      ...tagIds.map<FilterChip>((id) => ({
        kind: 'tag',
        value: id,
        label: `${t('SETTINGS_ACCESS.USERS.TAG')}: ${tagLabels[id] ?? id}`,
      })),
    ];
  });

  readonly filteredUsers = computed(() => {
    let result = this.users();
    const { statuses } = this.filterState();

    if (statuses.length > 0) {
      result = result.filter((u) => statuses.includes(u.status));
    }

    return result;
  });

  // Precompute everything the table cells need so the template binds plain properties
  // instead of calling N helper methods per row per change-detection tick. Recomputes only
  // when its signal deps change (users, rolesMap, permissions, currentUserId).
  readonly editableUsers = computed<UserRow[]>(() => {
    const rolesMap = this.rolesMap();
    return this.filteredUsers().map((user) => {
      const skills = [...((user as User & { skills?: UserSkill[] }).skills ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      const tags = [...(user.tags ?? [])].sort((a, b) => a.name.localeCompare(b.name));
      const hiddenSkills = skills.slice(2);
      const hiddenTags = tags.slice(2);

      const vm: UserRowVm = {
        displayName: this.getUserName(user),
        roleDisplay:
          user.roles && user.roles.length > 0
            ? user.roles
                .map((roleName: string) => {
                  const role = rolesMap.get(roleName);
                  return role ? this.formatRoleName(role.name) : this.formatRoleName(roleName);
                })
                .join(', ')
            : '-',
        visibleSkills: skills.slice(0, 2),
        hiddenSkillsCount: hiddenSkills.length,
        hiddenSkillsLabel: hiddenSkills.map((s) => s.name).join(', '),
        hasSkills: skills.length > 0,
        visibleTags: tags.slice(0, 2),
        hiddenTagsCount: hiddenTags.length,
        hiddenTagsLabel: hiddenTags.map((t) => t.name).join(', '),
        hasTags: tags.length > 0,
      };
      return { ...user, vm };
    });
  });

  readonly columns = computed<ColumnDef[]>(() => {
    // Read permission signals here so the columns array re-references when they change,
    // which (under OnPush) makes the data-table re-render and the menu visibility
    // predicates re-evaluate with the current values.
    const canUpdate = this.canUpdateUser();
    const canDelete = this.canDeleteUser();
    const canCreate = this.canCreateUser();
    const canTransfer = this.canTransferOwnership();
    const currentId = this.currentUserId();

    return [
      { field: 'name', title: 'SETTINGS_ACCESS.USERS.FIRST_NAME', type: 'custom', sortable: true },
      { field: 'email', title: 'SETTINGS_ACCESS.USERS.EMAIL', type: 'text', sortable: true },
      { field: '_roles', title: 'SETTINGS_ACCESS.USERS.ROLES', type: 'custom' },
      { field: '_skills', title: 'SETTINGS_ACCESS.USERS.SKILLS', type: 'custom' },
      { field: '_tags', title: 'SETTINGS_ACCESS.USERS.TAGS', type: 'custom' },
      {
        field: 'status',
        title: 'SETTINGS_ACCESS.USERS.STATUS',
        type: 'state',
        sortable: true,
        severityMap: {
          [UserStatusEnum.Active]: 'success',
          [UserStatusEnum.Inactive]: 'danger',
          [UserStatusEnum.Blocked]: 'warn',
        },
        labelMap: {
          [UserStatusEnum.Active]: 'Active',
          [UserStatusEnum.Inactive]: 'Inactive',
          [UserStatusEnum.Blocked]: 'Blocked',
        },
      },
      {
        field: '_actions',
        title: 'SETTINGS_ACCESS.USERS.ACTIONS',
        type: 'menu',
        menuItems: this.buildUserMenuItems({
          canUpdate,
          canDelete,
          canCreate,
          canTransfer,
          currentId,
        }),
      },
    ];
  });

  // Static menu-item definitions for the row action menu. Each item carries a per-row
  // `visible` predicate so items the user can't act on (no permission, wrong status,
  // self-row for Delete) simply don't render — no greyed-out noise.
  private buildUserMenuItems(perms: {
    canUpdate: boolean;
    canDelete: boolean;
    canCreate: boolean;
    canTransfer: boolean;
    currentId: string | null;
  }): MenuItemDef[] {
    const asUser = (row: unknown): User => row as User;
    return [
      {
        action: 'edit',
        label: 'GENERAL.EDIT',
        icon: 'pi pi-pencil',
        visible: () => perms.canUpdate,
      },
      {
        action: 'transferOwnership',
        label: 'SETTINGS_ACCESS.USERS.TRANSFER_OWNERSHIP',
        icon: 'pi pi-crown',
        visible: (row: unknown) =>
          perms.canTransfer &&
          asUser(row).status === UserStatusEnum.Active &&
          asUser(row).id !== perms.currentId,
      },
      {
        action: 'resendSetPasswordEmail',
        label: 'SETTINGS_ACCESS.USERS.RESEND_SET_PASSWORD_EMAIL',
        icon: 'pi pi-envelope',
        visible: (row: unknown) => perms.canCreate && asUser(row).canResendSetPasswordEmail,
      },
      {
        action: 'activate',
        label: 'SETTINGS_ACCESS.USERS.ACTIVATE_USER',
        icon: 'pi pi-user',
        visible: (row: unknown) => asUser(row).status === UserStatusEnum.Inactive,
      },
      {
        action: 'deactivate',
        label: 'SETTINGS_ACCESS.USERS.DEACTIVATE_USER',
        icon: 'pi pi-user',
        visible: (row: unknown) =>
          asUser(row).status === UserStatusEnum.Active && asUser(row).id !== perms.currentId,
      },
      {
        action: 'unblock',
        label: 'SETTINGS_ACCESS.USERS.UNBLOCK_USER',
        icon: 'pi pi-lock-open',
        visible: (row: unknown) => perms.canUpdate && asUser(row).status === UserStatusEnum.Blocked,
      },
      {
        action: 'delete',
        label: 'GENERAL.DELETE',
        icon: 'pi pi-trash',
        severity: 'danger',
        visible: (row: unknown) => perms.canDelete && asUser(row).id !== perms.currentId,
      },
    ];
  }

  onMenuActionClick(event: ActionClickEvent<UserRow>): void {
    const action = event.action as RowMenuActionId;
    const user = event.row;
    switch (action) {
      case 'edit':
        this.editUser(user);
        return;
      case 'transferOwnership':
        this.transferOwnership(user);
        return;
      case 'resendSetPasswordEmail':
        this.resendSetPasswordEmail(user);
        return;
      case 'activate':
        this.activateUser(user);
        return;
      case 'deactivate':
        this.deactivateUser(user);
        return;
      case 'unblock':
        this.unblockUser(user);
        return;
      case 'delete':
        this.deleteUser(user);
        return;
    }
  }

  // Store roles fetched from API
  readonly roles = signal<Role[]>([]);
  readonly skills = signal<Skill[]>([]);
  // Map role name (string) to Role object for quick lookup
  readonly rolesMap = computed(() => {
    const lookup = new Map<string, Role>();
    this.roles().forEach((role) => {
      if (role.name) {
        lookup.set(role.name, role);
      }
      if (role.id) {
        lookup.set(role.id, role);
      }
    });
    return lookup;
  });
  // Available roles for dropdown (using role ID as value, displaying role name as label).
  // `description` feeds the info tooltip on each option — show the role's grants
  // so the admin can see what powers come with assigning it.
  readonly availableRoles = computed(() => {
    return this.roles()
      .filter((role) => role.canAssign) // Only show roles the user can assign in dropdown
      .filter((role) => !!role.id)
      .map((role) => ({
        label: this.formatRoleName(role.name),
        value: role.id as string,
        description: role.grants?.length
          ? role.grants.join(', ')
          : this.translateService.instant('SETTINGS_ACCESS.USERS.ROLE_NO_GRANTS'),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  readonly skillsMap = computed(() => {
    const lookup = new Map<string, Skill>();
    this.skills().forEach((skill) => {
      lookup.set(skill.id, skill);
    });
    return lookup;
  });

  readonly availableSkills = computed(() =>
    this.skills().map((skill) => ({
      label: skill.name,
      value: skill.id,
    }))
  );

  // Helper to check if a role is assignable
  canAssignRole(roleId: string): boolean {
    const role = this.roles().find((r) => r.id === roleId);
    return role?.canAssign ?? false;
  }

  // Stable bound references handed to <tf-user-form>; defined once so the form's
  // input identity does not change on every parent change-detection cycle.
  readonly getRoleLabelFn = (roleId: string): string => this.getRoleLabel(roleId);
  readonly canAssignRoleFn = (roleId: string): boolean => this.canAssignRole(roleId);

  readonly hasAnyRoles = computed(() => {
    const formRoles = this.editUserForm.get('roles')?.value ?? [];
    return formRoles.length > 0;
  });
  readonly isEditFormValid = computed(() => {
    // Check email is valid
    const emailControl = this.editUserForm.get('email');
    if (!emailControl?.value || emailControl.invalid) {
      return false;
    }
    // Check has at least one role
    return this.hasAnyRoles();
  });

  constructor() {
    effect(() => {
      this.ensureSkillsLoaded();
    });
    this.setupSearchDebounce();
    this.rolesService.rolesUpdated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.loadRoles();
      this.loadUsers();
    });
    this.loadRoles();
    this.loadCurrentUser();
    this.loadUsers();
    provideUsersAiPageContext({
      users: this.users,
      searchQuery: this.searchQuery,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      originalUser: this.originalUser,
      isEditDialogVisible: this.isEditDialogVisible,
    });
  }

  canSaveEditForm(): boolean {
    // The email control is disabled (not editable), so its status is DISABLED and
    // `valid` would always be false — only check that a value is present.
    const emailControl = this.editUserForm.get('email');
    const rolesControl = this.editUserForm.get('roles');
    const hasEmail = !!emailControl?.value;
    const hasRoles = (rolesControl?.value?.length ?? 0) > 0;
    return !!(hasEmail && hasRoles);
  }

  private loadCurrentUser() {
    this.userService.getCurrentUser().subscribe({
      next: (user) => {
        this.currentUserId.set(user.id);
      },
      error: () => undefined,
    });
  }

  private loadRoles() {
    this.rolesService.getRoles().subscribe({
      next: (response) => {
        this.roles.set(response.items);
      },
      error: () => undefined,
    });
  }

  private loadSkills() {
    this.skillsLoadInProgress = true;
    this.skillsService
      .getAllSkills()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.skillsLoadInProgress = false;
        })
      )
      .subscribe({
        next: (skills) => {
          this.skills.set(skills);
          this.skillsLoaded = true;
        },
        error: () => undefined,
      });
  }

  private ensureSkillsLoaded(): void {
    if (!this.canReadSkills() || this.skillsLoaded || this.skillsLoadInProgress) {
      return;
    }
    this.loadSkills();
  }

  private loadTags(): void {
    this.tagsLoadInProgress = true;
    this.tagsService
      .getTags()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.tagsLoadInProgress = false;
        })
      )
      .subscribe({
        next: (response) => {
          this.availableTagOptions.set(
            response.items
              .filter(
                (t): t is TagResponse & { id: string; name: string; color: string } =>
                  !!t.id && !!t.name && !!t.color
              )
              .map((t) => ({ label: t.name, value: t.id, color: t.color }))
          );
          this.tagsLoaded = true;
        },
        error: () => undefined,
      });
  }

  private ensureTagsLoaded(): void {
    if (this.tagsLoaded || this.tagsLoadInProgress) {
      return;
    }
    this.loadTags();
  }

  private setupSearchDebounce() {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((query) => {
        this.searchQuery.set(query);
        this.first.set(0); // Reset to first page on search
        this.loadUsers(0, this.pageSize());
      });
  }

  private resolveApiStatus(): GetUsersStatusEnum {
    const statuses = this.filterState().statuses;
    if (statuses.length === 1) {
      return statuses[0] as GetUsersStatusEnum;
    }
    return GetUsersStatusEnum.All;
  }

  private loadUsers(page = 0, size = 50) {
    const hadLoadedOnce = this.hasLoadedOnce();
    this.isLoading.set(true);
    this.actionError.set(null);

    // Build sort array for backend
    const sortArray: string[] = [];
    const field = this.sortField();
    const order = this.sortOrder();
    if (field && order !== 0) {
      // Map table field names to backend field names
      const backendField = this.mapFieldToBackend(field);
      const direction = order === 1 ? 'asc' : 'desc';
      sortArray.push(`${backendField}:${direction}`);
    }

    const { roleIds, skillIds, tagIds, tagLabels } = this.filterState();
    // Backend filters users by tag name (OR semantics) — convert IDs from the picker → names.
    // IDs that no longer have a known label (rare: tag deleted while filter is open) are dropped.
    const tagNames = tagIds.map((id) => tagLabels[id]).filter((name): name is string => !!name);

    this.userService
      .getUsersData({
        page,
        size,
        sort: sortArray.length > 0 ? sortArray : undefined,
        search: nonEmptyOrUndefined(this.searchQuery()),
        status: this.resolveApiStatus(),
        roleIds: roleIds.length > 0 ? roleIds : undefined,
        skillIds: skillIds.length > 0 ? skillIds : undefined,
        tagNames: tagNames.length > 0 ? tagNames : undefined,
      })
      .subscribe({
        next: (response: UserList) => {
          // Set totalRecords FIRST before setting users
          // This ensures PrimeNG calculates pagination correctly
          this.totalRecords.set(
            (response.page as any)?.total_elements ?? response.page?.totalElements ?? 0
          );
          // Then set the users
          this.users.set(response.items);
          this.isLoading.set(false);
          this.loadError.set(null);
          this.loadErrorDetails.set(null);
          this.initialLoadFailed.set(false);
          this.hasLoadedOnce.set(true);
        },
        error: (error: unknown) => {
          this.isLoading.set(false);
          const message = this.translateService.instant('SETTINGS.LOAD_ERROR.TITLE');
          if (hadLoadedOnce) {
            this.hasLoadedOnce.set(true);
            this.actionError.set(message);
          } else {
            this.loadError.set(message);
            this.initialLoadFailed.set(true);
            void this.updateLoadErrorDetails(error);
          }
        },
      });
  }

  private mapFieldToBackend(field: string): string {
    // Map table column field names to backend field names
    // Backend uses camelCase for sorting (e.g., 'createdAt:asc' per API docs)
    // Format: property(:asc|desc) - e.g., 'email:asc', 'status:desc'
    const fieldMap: Record<string, string> = {
      name: 'firstname', // Sort by firstName since name is computed from firstname + lastname
      email: 'email',
      status: 'status',
    };
    return fieldMap[field] ?? field;
  }

  onSort(event: SortEvent): void {
    // PrimeNG sort event: field is the column field, order is 1 (asc) or -1 (desc)
    // Check if sort actually changed to prevent infinite loops
    const newField = event.field ?? null;
    const newOrder = event.order ?? 0;

    if (this.sortField() === newField && this.sortOrder() === newOrder) {
      return; // Sort hasn't changed, don't reload
    }

    this.sortField.set(newField);
    this.sortOrder.set(newOrder);

    // Reload users with new sort
    this.first.set(0); // Reset to first page on sort
    this.loadUsers(0, this.pageSize());
  }

  private formatRoleName(roleName: string | undefined): string {
    if (!roleName) {
      return '';
    }

    return roleName
      .split(/[_\s]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  onPageChange(event: TfPageEvent): void {
    this.first.set(event.first);
    this.pageSize.set(event.rows);
    const page = Math.floor(event.first / event.rows);
    this.loadUsers(page, event.rows);
  }

  reloadUsers(): void {
    // Reload users from the first page with current search and sort settings
    this.first.set(0); // Reset to first page
    this.loadUsers(0, this.pageSize());
  }

  /**
   * Refetch the page the user is currently viewing at the currently selected page size.
   * Used after in-place actions (activate/deactivate/edit/…) so the paginator position
   * and page size are preserved instead of jumping back to page 0.
   */
  private reloadCurrentPage(): void {
    const size = this.pageSize();
    const page = Math.floor(this.first() / size);
    this.loadUsers(page, size);
  }

  onSearchChange(query: string): void {
    // Emit to subject for debounced search
    this.searchSubject.next(query);
  }

  removeFilter(kind: FilterChipKind, value: string): void {
    switch (kind) {
      case 'status':
        this.removeStatusFilter(value);
        return;
      case 'role':
        this.removeRoleFilter(value);
        return;
      case 'skill':
        this.removeSkillFilter(value);
        return;
      case 'tag':
        this.removeTagFilter(value);
        return;
    }
  }

  removeStatusFilter(status: string): void {
    const current = this.filterState();
    this.filterState.set({ ...current, statuses: current.statuses.filter((s) => s !== status) });
    this.first.set(0);
    this.loadUsers(0, this.pageSize());
  }

  removeRoleFilter(roleId: string): void {
    const current = this.filterState();
    this.filterState.set({ ...current, roleIds: current.roleIds.filter((id) => id !== roleId) });
    this.first.set(0);
    this.loadUsers(0, this.pageSize());
  }

  removeSkillFilter(skillId: string): void {
    const current = this.filterState();
    this.filterState.set({ ...current, skillIds: current.skillIds.filter((id) => id !== skillId) });
    this.first.set(0);
    this.loadUsers(0, this.pageSize());
  }

  clearAllFilters(): void {
    this.filterState.set({
      statuses: [],
      roleIds: [],
      skillIds: [],
      skillLabels: {},
      tagIds: [],
      tagLabels: {},
      tagColors: {},
    });
    this.first.set(0);
    this.loadUsers(0, this.pageSize());
  }

  removeTagFilter(tagId: string): void {
    const current = this.filterState();
    const tagIds = current.tagIds.filter((id) => id !== tagId);
    const tagLabels = { ...current.tagLabels };
    delete tagLabels[tagId];
    const tagColors = { ...current.tagColors };
    delete tagColors[tagId];
    this.filterState.set({ ...current, tagIds, tagLabels, tagColors });
    this.first.set(0);
    this.loadUsers(0, this.pageSize());
  }

  openFiltersDialog(): void {
    const { statuses, roleIds, skillIds, tagIds } = this.filterState();
    const dialogRef = this.dialogService.open(UserFiltersDialogComponent, {
      header: this.translateService.instant('SETTINGS_ACCESS.USERS.FILTERS_DIALOG_TITLE'),
      width: 'min(31.25rem, 95vw)',
      modal: true,
      closeOnEscape: true,
      dismissableMask: true,
      data: { statuses, roleIds, skillIds, tagIds } satisfies UserFiltersDialogData,
    });

    dialogRef?.onClose.subscribe((result: UserFiltersDialogResult | undefined) => {
      if (result) {
        this.filterState.set(result);
        this.first.set(0);
        this.loadUsers(0, this.pageSize());
      }
    });
  }

  editUser(user: User): void {
    this.ensureSkillsLoaded();
    this.ensureTagsLoaded();
    this.originalUser.set(user); // Store original user for lockVersion
    this.originalUserRoles.set([...user.roles]); // Store original roles
    this.originalUserSkillIds.set(this.getUserSkillIds(user));
    this.originalUserTagIds.set((user.tags ?? []).map((t) => t.id));
    const rolesMap = this.rolesMap();

    // Convert role names to role IDs for the edit form
    // If roles haven't loaded yet, wait for them
    if (rolesMap.size === 0 && this.roles().length === 0) {
      // Roles not loaded yet, load them first
      this.rolesService.getRoles().subscribe({
        next: (response) => {
          this.roles.set(response.items);
          // Retry after roles are loaded
          this.setEditingUserWithRoleIds(user);
        },
        error: () => {
          // Still open dialog with empty roles if loading fails
          this.setEditingUserWithRoleIds(user);
        },
      });
    } else {
      this.setEditingUserWithRoleIds(user);
    }
  }

  private setEditingUserWithRoleIds(user: User) {
    const rolesMap = this.rolesMap();

    // Build list of role IDs from user's roles
    const roleIds: string[] = [];

    user.roles.forEach((roleIdentifier: string) => {
      // First try direct lookup (works for both names and IDs since we map both)
      let role = rolesMap.get(roleIdentifier);

      // If not found, try case-insensitive name match
      role ??= this.roles().find((r) => r.name.toLowerCase() === roleIdentifier.toLowerCase());

      if (role) {
        // We have access to this role - add its ID
        if (role.id) {
          roleIds.push(role.id);
        }
      }
    });

    // Populate form with role IDs. emitEvent:false avoids 6 valueChanges-driven
    // CD passes through the 50-row table while the dialog is opening.
    this.editUserForm.patchValue(
      {
        firstname: user.firstname ?? '',
        lastname: user.lastname ?? '',
        email: user.email ?? '',
        roles: roleIds,
        skills: this.getUserSkillIds(user),
        tags: (user.tags ?? []).map((t) => t.id),
      },
      { emitEvent: false }
    );
    this.editServerErrors.clear();

    this.isEditDialogVisible.set(true);
  }

  saveEditing(): void {
    if (this.editUserForm.invalid) {
      this.editUserForm.markAllAsTouched();
      return;
    }

    const original = this.originalUser();
    if (!original) return;

    const formValue = this.editUserForm.getRawValue();

    const lockVersion = this.getUserLockVersion(original);
    const selectedSkillIds = this.normalizeIds(formValue.skills);
    const selectedTagIds = this.normalizeIds(formValue.tags);
    const shouldUpdateSkills =
      this.canAssignSkillsOnEdit() &&
      this.haveIdsChanged(this.originalUserSkillIds(), selectedSkillIds);
    const shouldUpdateTags = this.haveIdsChanged(this.originalUserTagIds(), selectedTagIds);

    // Build PatchUser with all edited fields
    const patchUser: PatchUser = {
      lockVersion,
      roleIds: new Set(formValue.roles ?? []),
    };

    // Include firstname if it was edited
    if (formValue.firstname !== undefined && formValue.firstname !== null) {
      patchUser.firstname = formValue.firstname.trim();
    }

    // Include lastname if it was edited
    if (formValue.lastname !== undefined && formValue.lastname !== null) {
      patchUser.lastname = formValue.lastname.trim();
    }

    this.editServerErrors.clear();
    this.isLoading.set(true);
    this.isSaving.set(true);
    this.userService
      .editUser(original.id, patchUser)
      .pipe(
        switchMap((updatedUser) => {
          if (!shouldUpdateSkills) {
            return of({
              user: updatedUser,
              skillsUpdateFailed: false,
              tagsUpdateFailed: false,
            } satisfies EditUserSaveResult);
          }

          const updateUserSkills: UpdateUserSkills = {
            skillIds: new Set(selectedSkillIds),
            lockVersion: this.getUserLockVersion(updatedUser),
          };

          return this.userService.updateUserSkills(updatedUser.id, updateUserSkills).pipe(
            map(
              (user) =>
                ({
                  user,
                  skillsUpdateFailed: false,
                  tagsUpdateFailed: false,
                }) satisfies EditUserSaveResult
            ),
            catchError((error: unknown) =>
              of({
                user: updatedUser,
                skillsUpdateFailed: true,
                tagsUpdateFailed: false,
                error,
              } satisfies EditUserSaveResult)
            )
          );
        }),
        switchMap((result) => {
          // Skip tag update if not changed, or if a prior step in the chain failed (since the
          // returned user's lockVersion may not have advanced as expected).
          if (!shouldUpdateTags || result.skillsUpdateFailed) {
            return of(result);
          }

          const updateUserTags: UpdateUserTags = {
            tagIds: new Set(selectedTagIds),
            lockVersion: this.getUserLockVersion(result.user),
          };

          return this.userService.updateUserTags(result.user.id, updateUserTags).pipe(
            map(
              (user) =>
                ({
                  user,
                  skillsUpdateFailed: false,
                  tagsUpdateFailed: false,
                }) satisfies EditUserSaveResult
            ),
            catchError((error: unknown) =>
              of({
                user: result.user,
                skillsUpdateFailed: false,
                tagsUpdateFailed: true,
                error,
              } satisfies EditUserSaveResult)
            )
          );
        }),
        finalize(() => this.isSaving.set(false))
      )
      .subscribe({
        next: async (result) => {
          const updatedUser = result.user;
          // Update local state with the response
          const updatedUsers = this.users().map((user) =>
            user.id === updatedUser.id ? updatedUser : user
          );
          this.users.set(updatedUsers);
          this.isLoading.set(false);
          // Reload users to ensure we have the latest data
          this.reloadCurrentPage();

          if (result.skillsUpdateFailed) {
            this.originalUser.set(updatedUser);
            this.originalUserSkillIds.set(this.getUserSkillIds(updatedUser));
            await this.editServerErrors.apply(result.error);
            this.actionError.set(
              this.translateService.instant(
                'SETTINGS_ACCESS.USERS.USER_EDIT_PARTIAL_SKILLS_FAILED'
              )
            );
            return;
          }

          if (result.tagsUpdateFailed) {
            this.originalUser.set(updatedUser);
            this.originalUserTagIds.set((updatedUser.tags ?? []).map((t) => t.id));
            this.editUserForm.patchValue({
              tags: (updatedUser.tags ?? []).map((t) => t.id),
            });
            await this.editServerErrors.apply(result.error);
            this.actionError.set(
              this.translateService.instant('SETTINGS_ACCESS.USERS.USER_EDIT_PARTIAL_TAGS_FAILED')
            );
            return;
          }

          this.closeEditDialog();
        },
        error: async (error: unknown) => {
          this.isLoading.set(false);
          const applied = await this.editServerErrors.apply(error);
          if (applied && applied.fieldCount > 0) {
            if (applied.generalMessages.length > 0) {
              this.actionError.set(applied.generalMessages.join(' '));
            }
            return;
          }
          const joinedGeneral = applied?.generalMessages.join(' ') ?? '';
          const fallback =
            joinedGeneral !== ''
              ? joinedGeneral
              : this.translateService.instant('SETTINGS_ACCESS.USERS.USER_EDIT_FAILED');
          this.actionError.set(fallback);
        },
      });
  }

  closeEditDialog(): void {
    this.isEditDialogVisible.set(false);
    this.editUserForm.reset();
    this.originalUser.set(null);
    this.originalUserRoles.set([]);
    this.originalUserSkillIds.set([]);
    this.originalUserTagIds.set([]);
    this.editServerErrors.clear();
    this.actionError.set(null);
  }

  getStatusDisplay(status: UserStatusEnum | string): string {
    const keyMap: Record<string, string> = {
      [UserStatusEnum.Active]: 'SETTINGS_ACCESS.USERS.STATUS_ACTIVE',
      [UserStatusEnum.Inactive]: 'SETTINGS_ACCESS.USERS.STATUS_INACTIVE',
      [UserStatusEnum.Blocked]: 'SETTINGS_ACCESS.USERS.STATUS_BLOCKED',
    };
    const key = keyMap[status];
    return key ? this.translateService.instant(key) : status;
  }

  getStatusLabel(status: UserStatusEnum | string): string {
    return this.getStatusDisplay(status);
  }

  getStatusSeverity(status: UserStatusEnum | string): 'success' | 'danger' | 'warn' | 'secondary' {
    switch (status) {
      case UserStatusEnum.Active:
        return 'success';
      case UserStatusEnum.Inactive:
        return 'danger';
      case UserStatusEnum.Blocked:
        return 'warn';
      default:
        return 'secondary';
    }
  }

  getRoleDisplay(roleNames: string[]): string {
    if (!roleNames || roleNames.length === 0) {
      return '-';
    }
    const rolesMap = this.rolesMap();
    return roleNames
      .map((roleName: string) => {
        const role = rolesMap.get(roleName);
        return role ? this.formatRoleName(role.name) : this.formatRoleName(roleName);
      })
      .join(', ');
  }

  getUserSkills(user: User): UserSkill[] {
    return [...((user as User & { skills?: UserSkill[] }).skills ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }

  getVisibleUserSkills(user: User): UserSkill[] {
    return this.getUserSkills(user).slice(0, 2);
  }

  getHiddenUserSkillsCount(user: User): number {
    return Math.max(this.getUserSkills(user).length - 2, 0);
  }

  getHiddenUserSkillsLabel(user: User): string {
    return this.getUserSkills(user)
      .slice(2)
      .map((s) => s.name)
      .join(', ');
  }

  getUserDisplayTags(user: User): UserTag[] {
    return [...(user.tags ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  }

  getVisibleUserTags(user: User): UserTag[] {
    return this.getUserDisplayTags(user).slice(0, 2);
  }

  getHiddenUserTagsCount(user: User): number {
    return Math.max(this.getUserDisplayTags(user).length - 2, 0);
  }

  getHiddenUserTagsLabel(user: User): string {
    return this.getUserDisplayTags(user)
      .slice(2)
      .map((t) => t.name)
      .join(', ');
  }

  getSkillLabel(skillId: string): string {
    return this.skillsMap().get(skillId)?.name ?? skillId;
  }

  getUserName(user: Partial<User>): string {
    if (user.firstname || user.lastname) {
      return [user.firstname, user.lastname].filter(Boolean).join(' ').trim();
    }
    return user.email ?? '';
  }

  private getUserSkillIds(user: User): string[] {
    return this.getUserSkills(user).map((skill) => skill.id);
  }

  private normalizeIds(values: string[] | null | undefined): string[] {
    return Array.from(new Set((values ?? []).filter((value): value is string => !!value)));
  }

  private haveIdsChanged(previousIds: string[], nextIds: string[]): boolean {
    const previous = [...previousIds].sort();
    const next = [...nextIds].sort();

    if (previous.length !== next.length) {
      return true;
    }

    return previous.some((value, index) => value !== next[index]);
  }

  private getUserLockVersion(user: User): number {
    const userWithLockVersion = user as User & {
      lockVersion?: number;
      lock_version?: number;
    };
    return userWithLockVersion.lockVersion ?? userWithLockVersion.lock_version ?? 0;
  }

  createUser(): void {
    this.ensureSkillsLoaded();
    this.ensureTagsLoaded();
    this.actionError.set(null);
    this.createUserForm.reset({
      firstname: '',
      lastname: '',
      email: '',
      roles: [],
      skills: [],
      tags: [],
    });
    this.createServerErrors.clear();
    this.isCreateDialogVisible.set(true);
  }

  saveNewUser(): void {
    if (this.createUserForm.invalid) {
      this.createUserForm.markAllAsTouched();
      const formValue = this.createUserForm.getRawValue();

      if (!formValue.email || !formValue.firstname || !formValue.lastname) {
        this.actionError.set(
          this.translateService.instant('SETTINGS_ACCESS.USERS.REQUIRED_FIELDS_MISSING')
        );
        return;
      }

      if (!formValue.roles || formValue.roles.length === 0) {
        this.actionError.set(this.translateService.instant('SETTINGS_ACCESS.USERS.ROLES_REQUIRED'));
        return;
      }
      return;
    }

    const formValue = this.createUserForm.getRawValue();
    const validRoleIds = (formValue.roles ?? []).filter(
      (id): id is string => !!id && typeof id === 'string'
    );
    const validSkillIds = this.canAssignSkillsOnCreate() ? this.normalizeIds(formValue.skills) : [];
    const validTagIds = this.normalizeIds(formValue.tags);

    const createUser: CreateUser = {
      email: (formValue.email ?? '').trim(),
      firstname: (formValue.firstname ?? '').trim(),
      lastname: (formValue.lastname ?? '').trim(),
      roleIds: new Set(validRoleIds),
    };
    if (validSkillIds.length > 0) {
      createUser.skillIds = new Set(validSkillIds);
    }
    if (validTagIds.length > 0) {
      createUser.tagIds = new Set(validTagIds);
    }

    this.createServerErrors.clear();
    this.actionError.set(null);
    this.isLoading.set(true);
    this.isSaving.set(true);
    this.userService
      .createUser(createUser)
      .pipe(finalize(() => this.isSaving.set(false)))
      .subscribe({
        next: () => {
          this.isLoading.set(false);
          this.closeCreateDialog();
          // Reload users to get the latest data including the new user
          this.reloadCurrentPage();
        },
        error: async (error: unknown) => {
          this.isLoading.set(false);
          const applied = await this.createServerErrors.apply(error);
          if (applied && applied.fieldCount > 0) {
            if (applied.generalMessages.length > 0) {
              this.actionError.set(applied.generalMessages.join(' '));
            }
            return;
          }
          const joinedGeneral = applied?.generalMessages.join(' ') ?? '';
          const fallback =
            joinedGeneral !== ''
              ? joinedGeneral
              : this.translateService.instant('SETTINGS_ACCESS.USERS.USER_CREATE_FAILED');
          this.actionError.set(fallback);
        },
      });
  }

  closeCreateDialog(): void {
    this.isCreateDialogVisible.set(false);
    this.createUserForm.reset({
      firstname: '',
      lastname: '',
      email: '',
      roles: [],
      skills: [],
      tags: [],
    });
    this.createServerErrors.clear();
    this.actionError.set(null);
  }

  deleteUser(user: User): void {
    const userName = this.getUserName(user);
    const translatedMessage = this.translateService.instant(
      'SETTINGS_ACCESS.USERS.DELETE_CONFIRM_MESSAGE'
    );
    const message = translatedMessage.replace('{name}', userName);

    this.confirmationService.confirm({
      message,
      header: this.translateService.instant('SETTINGS_ACCESS.USERS.DELETE_CONFIRM_TITLE'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.isLoading.set(true);
        this.userService.deleteUser(user.id).subscribe({
          next: () => {
            this.isLoading.set(false);
            this.reloadCurrentPage();
          },
          error: (error: unknown) => {
            this.isLoading.set(false);
            let errorMessage = this.translateService.instant('SETTINGS_ACCESS.USERS.DELETE_FAILED');
            if (error && typeof error === 'object' && 'error' in error) {
              const httpError = error as { error?: unknown; status?: number };
              if (httpError.error && typeof httpError.error === 'object') {
                const errorBody = httpError.error as Record<string, unknown>;
                if (errorBody.message && typeof errorBody.message === 'string') {
                  errorMessage = errorBody.message;
                }
              }
            }
            this.actionError.set(errorMessage);
          },
        });
      },
    });
  }

  activateUser(user: User): void {
    const userName = this.getUserName(user);
    const translatedMessage = this.translateService.instant(
      'SETTINGS_ACCESS.USERS.ACTIVATE_CONFIRM_MESSAGE'
    );
    const message = translatedMessage.replace('{name}', userName);

    this.confirmationService.confirm({
      message,
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-secondary',
      header: this.translateService.instant('SETTINGS_ACCESS.USERS.ACTIVATE_CONFIRM_TITLE'),
      accept: () => {
        this.isLoading.set(true);
        this.userService.activateUser(user.id).subscribe({
          next: (_updatedUser) => {
            this.isLoading.set(false);
            this.reloadCurrentPage();
          },
          error: (error: unknown) => {
            this.isLoading.set(false);
            let errorMessage = this.translateService.instant(
              'SETTINGS_ACCESS.USERS.ACTIVATE_FAILED'
            );
            if (error && typeof error === 'object' && 'error' in error) {
              const httpError = error as { error?: unknown; status?: number };
              if (httpError.error && typeof httpError.error === 'object') {
                const errorBody = httpError.error as Record<string, unknown>;
                if (errorBody.message && typeof errorBody.message === 'string') {
                  errorMessage = errorBody.message;
                }
              }
            }
            this.actionError.set(errorMessage);
          },
        });
      },
    });
  }

  deactivateUser(user: User): void {
    const userName = this.getUserName(user);
    const translatedMessage = this.translateService.instant(
      'SETTINGS_ACCESS.USERS.DEACTIVATE_CONFIRM_MESSAGE'
    );
    const message = translatedMessage.replace('{name}', userName);

    this.confirmationService.confirm({
      message,
      header: this.translateService.instant('SETTINGS_ACCESS.USERS.DEACTIVATE_CONFIRM_TITLE'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.isLoading.set(true);
        this.userService.deactivateUser(user.id).subscribe({
          next: (_updatedUser) => {
            this.isLoading.set(false);
            this.reloadCurrentPage();
          },
          error: (error: unknown) => {
            this.isLoading.set(false);
            let errorMessage = this.translateService.instant(
              'SETTINGS_ACCESS.USERS.DEACTIVATE_FAILED'
            );
            if (error && typeof error === 'object' && 'error' in error) {
              const httpError = error as { error?: unknown; status?: number };
              if (httpError.error && typeof httpError.error === 'object') {
                const errorBody = httpError.error as Record<string, unknown>;
                if (errorBody.message && typeof errorBody.message === 'string') {
                  errorMessage = errorBody.message;
                }
              }
            }
            this.actionError.set(errorMessage);
          },
        });
      },
    });
  }

  transferOwnership(user: User): void {
    const userName = this.getUserName(user);
    const translatedMessage = this.translateService.instant(
      'SETTINGS_ACCESS.USERS.TRANSFER_OWNERSHIP_CONFIRM_MESSAGE'
    );
    const message = translatedMessage.replace('{name}', userName);

    this.confirmationService.confirm({
      message,
      header: this.translateService.instant(
        'SETTINGS_ACCESS.USERS.TRANSFER_OWNERSHIP_CONFIRM_TITLE'
      ),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.isLoading.set(true);
        this.userService.makeUserOwner(user.id).subscribe({
          next: () => {
            this.isLoading.set(false);
            this.loadCurrentUser();
            this.reloadCurrentPage();
          },
          error: (error: unknown) => {
            this.isLoading.set(false);
            this.actionError.set(
              this.getActionErrorMessage(error, 'SETTINGS_ACCESS.USERS.TRANSFER_OWNERSHIP_FAILED')
            );
          },
        });
      },
    });
  }

  resendSetPasswordEmail(user: User): void {
    const translatedMessage = this.translateService.instant(
      'SETTINGS_ACCESS.USERS.RESEND_SET_PASSWORD_CONFIRM_MESSAGE'
    );
    const message = translatedMessage.replace('{email}', user.email);

    this.confirmationService.confirm({
      message,
      header: this.translateService.instant(
        'SETTINGS_ACCESS.USERS.RESEND_SET_PASSWORD_CONFIRM_TITLE'
      ),
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.isLoading.set(true);
        this.userService.resendSetPasswordEmail(user.id).subscribe({
          next: () => {
            this.isLoading.set(false);
            this.reloadCurrentPage();
          },
          error: (error: unknown) => {
            this.isLoading.set(false);
            this.actionError.set(
              this.getActionErrorMessage(error, 'SETTINGS_ACCESS.USERS.RESEND_SET_PASSWORD_FAILED')
            );
          },
        });
      },
    });
  }

  unblockUser(user: User): void {
    const userName = this.getUserName(user);
    const translatedMessage = this.translateService.instant(
      'SETTINGS_ACCESS.USERS.UNBLOCK_CONFIRM_MESSAGE'
    );
    const message = translatedMessage.replace('{name}', userName);

    this.confirmationService.confirm({
      message,
      header: this.translateService.instant('SETTINGS_ACCESS.USERS.UNBLOCK_CONFIRM_TITLE'),
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.isLoading.set(true);
        this.userService.unblockUser(user.id).subscribe({
          next: () => {
            this.isLoading.set(false);
            this.reloadCurrentPage();
          },
          error: (error: unknown) => {
            this.isLoading.set(false);
            this.actionError.set(
              this.getActionErrorMessage(error, 'SETTINGS_ACCESS.USERS.UNBLOCK_FAILED')
            );
          },
        });
      },
    });
  }

  canActivateUser(user: User): boolean {
    return user.status === UserStatusEnum.Inactive;
  }

  canDeactivateUser(user: User): boolean {
    return user.status === UserStatusEnum.Active && !this.isCurrentUser(user);
  }

  canTransferOwnershipTo(user: User): boolean {
    return (
      this.canTransferOwnership() &&
      user.status === UserStatusEnum.Active &&
      !this.isCurrentUser(user)
    );
  }

  canResendSetPasswordEmail(user: User): boolean {
    return this.canCreateUser() && user.canResendSetPasswordEmail;
  }

  canUnblockUser(user: User): boolean {
    return this.canUpdateUser() && user.status === UserStatusEnum.Blocked;
  }

  isCurrentUser(user: User): boolean {
    return this.currentUserId() === user.id;
  }

  private getActionErrorMessage(error: unknown, fallbackKey: string): string {
    let errorMessage = this.translateService.instant(fallbackKey);
    if (error && typeof error === 'object' && 'error' in error) {
      const httpError = error as { error?: unknown };
      if (httpError.error && typeof httpError.error === 'object') {
        const errorBody = httpError.error as Record<string, unknown>;
        if (errorBody.message && typeof errorBody.message === 'string') {
          errorMessage = errorBody.message;
        }
      }
    }
    return errorMessage;
  }

  clearActionError(): void {
    this.actionError.set(null);
  }

  private async updateLoadErrorDetails(error: unknown): Promise<void> {
    this.loadErrorDetails.set(await resolveSettingsLoadErrorDetails(error));
  }

  getRoleLabel(roleId: string): string {
    // First try to find in available roles
    const option = this.availableRoles().find((item) => item.value === roleId);
    if (option) {
      return option.label;
    }
    // If not found, check in all roles (including non-assignable)
    const role = this.roles().find((r) => r.id === roleId);
    if (role) {
      return this.formatRoleName(role.name);
    }
    return roleId;
  }
}
